import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  IconButton,
  Avatar,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Fade,
  Slide
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Home as HomeIcon,
  Work as WorkIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { gsap } from 'gsap';
import API_CONFIG from '../../config/api.config';
import customerAuthService from '../../services/customerAuthService';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import useCustomerSessionTimeout from '../../hooks/useCustomerSessionTimeout';

const CustomerProfilePage = () => {
  const { websitePath } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);

  // Profile data
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  // Addresses (stored in localStorage for now, DB later)
  const [addresses, setAddresses] = useState([]);

  // New/Edit address form
  const [addressForm, setAddressForm] = useState({
    type: 'home', // 'home', 'work', 'other'
    label: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    isDefault: false
  });

  // Animation refs
  const headerRef = useRef(null);
  const profileCardRef = useRef(null);
  const addressesRef = useRef(null);

  // Session timeout tracking
  useCustomerSessionTimeout();

  useEffect(() => {
    const currentCustomer = customerAuthService.getCurrentCustomer();
    if (!currentCustomer) {
      navigate(`/${websitePath}`);
      return;
    }

    setCustomer(currentCustomer);
    setProfileData({
      name: currentCustomer.name || '',
      email: currentCustomer.email || '',
      phone: currentCustomer.phone || ''
    });

    // Load addresses from localStorage
    const savedAddresses = localStorage.getItem(`customer_addresses_${currentCustomer.id}`);
    if (savedAddresses) {
      setAddresses(JSON.parse(savedAddresses));
    }

    setLoading(false);

    // Animate entrance
    gsap.fromTo(headerRef.current,
      { opacity: 0, y: -30 },
      { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }
    );

    gsap.fromTo(profileCardRef.current,
      { opacity: 0, scale: 0.95 },
      { opacity: 1, scale: 1, duration: 0.5, delay: 0.2, ease: 'back.out(1.2)' }
    );

    gsap.fromTo(addressesRef.current,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.5, delay: 0.4, ease: 'power2.out' }
    );
  }, [websitePath, navigate]);

  const handleEdit = () => {
    setEditMode(true);
    gsap.to(profileCardRef.current, {
      scale: 1.02,
      duration: 0.3,
      ease: 'power2.out'
    });
  };

  const handleCancel = () => {
    setEditMode(false);
    // Reset to original data
    if (customer) {
      setProfileData({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || ''
      });
    }
    gsap.to(profileCardRef.current, {
      scale: 1,
      duration: 0.3,
      ease: 'power2.out'
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save to localStorage for now (DB later)
      const updatedCustomer = {
        ...customer,
        ...profileData
      };
      localStorage.setItem('customer_data', JSON.stringify(updatedCustomer));
      setCustomer(updatedCustomer);
      setEditMode(false);

      // Success animation
      gsap.to(profileCardRef.current, {
        scale: 1,
        duration: 0.3,
        ease: 'power2.out'
      });

      // Show success message (you can add a snackbar here)
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAddressDialog = (index = null) => {
    if (index !== null) {
      setEditingAddressIndex(index);
      setAddressForm(addresses[index]);
    } else {
      setEditingAddressIndex(null);
      setAddressForm({
        type: 'home',
        label: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        pincode: '',
        phone: '',
        isDefault: addresses.length === 0
      });
    }
    setAddressDialogOpen(true);
  };

  const handleCloseAddressDialog = () => {
    setAddressDialogOpen(false);
    setEditingAddressIndex(null);
    setAddressForm({
      type: 'home',
      label: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      phone: '',
      isDefault: false
    });
  };

  const handleSaveAddress = () => {
    const newAddresses = [...addresses];

    if (editingAddressIndex !== null) {
      // Update existing address
      newAddresses[editingAddressIndex] = { ...addressForm };
    } else {
      // Add new address
      newAddresses.push({ ...addressForm });
    }

    // If this is set as default, unset others
    if (addressForm.isDefault) {
      newAddresses.forEach((addr, idx) => {
        if (idx !== editingAddressIndex && idx !== newAddresses.length - 1) {
          addr.isDefault = false;
        }
      });
    }

    setAddresses(newAddresses);

    // Save to localStorage
    if (customer) {
      localStorage.setItem(`customer_addresses_${customer.id}`, JSON.stringify(newAddresses));
    }

    handleCloseAddressDialog();

    // Animate new address card
    gsap.fromTo(`#address-${newAddresses.length - 1}`,
      { opacity: 0, scale: 0.8, y: 20 },
      { opacity: 1, scale: 1, y: 0, duration: 0.5, ease: 'back.out(1.2)' }
    );
  };

  const handleDeleteAddress = (index) => {
    const newAddresses = addresses.filter((_, idx) => idx !== index);
    setAddresses(newAddresses);

    if (customer) {
      localStorage.setItem(`customer_addresses_${customer.id}`, JSON.stringify(newAddresses));
    }

    // Animate removal
    gsap.to(`#address-${index}`, {
      opacity: 0,
      scale: 0.8,
      height: 0,
      marginBottom: 0,
      duration: 0.3,
      ease: 'power2.in',
      onComplete: () => {
        // Card will be removed from DOM
      }
    });
  };

  const getAddressIcon = (type) => {
    switch (type) {
      case 'home': return <HomeIcon />;
      case 'work': return <WorkIcon />;
      default: return <LocationIcon />;
    }
  };

  const getAddressTypeLabel = (type) => {
    switch (type) {
      case 'home': return 'Home';
      case 'work': return 'Work';
      default: return 'Other';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!customer) {
    return null;
  }

  // Create theme based on website (you can fetch this from websiteData)
  const theme = createTheme({
    palette: {
      primary: { main: '#1a237e' },
      secondary: { main: '#c51162' }
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', py: 4 }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box ref={headerRef} sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              component={RouterLink}
              to={`/${websitePath}`}
              sx={{ bgcolor: 'white', boxShadow: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" fontWeight={700} sx={{ flexGrow: 1 }}>
              My Profile
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Profile Card */}
            <Grid item xs={12} md={4}>
              <Fade in timeout={600}>
                <Card
                  ref={profileCardRef}
                  sx={{
                    borderRadius: 4,
                    boxShadow: 4,
                    overflow: 'hidden',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)',
                      transition: 'all 0.3s ease'
                    }
                  }}
                >
                  {/* Gradient Header */}
                  <Box
                    sx={{
                      height: 120,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 100,
                        height: 100,
                        border: '4px solid white',
                        bgcolor: 'rgba(255,255,255,0.2)',
                        fontSize: '2.5rem',
                        fontWeight: 700
                      }}
                    >
                      {customer.name?.charAt(0)?.toUpperCase() || 'U'}
                    </Avatar>
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    {!editMode ? (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                          <Button
                            startIcon={<EditIcon />}
                            onClick={handleEdit}
                            variant="outlined"
                            size="small"
                          >
                            Edit
                          </Button>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                          <Typography variant="h5" fontWeight={700} gutterBottom>
                            {profileData.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'text.secondary' }}>
                            <EmailIcon fontSize="small" />
                            <Typography variant="body2">{profileData.email}</Typography>
                          </Box>
                          {profileData.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                              <PhoneIcon fontSize="small" />
                              <Typography variant="body2">{profileData.phone}</Typography>
                            </Box>
                          )}
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
                          <Button
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            variant="contained"
                            size="small"
                            disabled={saving}
                          >
                            {saving ? 'Saving...' : 'Save'}
                          </Button>
                          <Button
                            startIcon={<CancelIcon />}
                            onClick={handleCancel}
                            variant="outlined"
                            size="small"
                          >
                            Cancel
                          </Button>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <TextField
                            label="Full Name"
                            value={profileData.name}
                            onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                            fullWidth
                            size="small"
                          />
                          <TextField
                            label="Email"
                            value={profileData.email}
                            onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                            fullWidth
                            size="small"
                            type="email"
                            disabled
                          />
                          <TextField
                            label="Phone"
                            value={profileData.phone}
                            onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                            fullWidth
                            size="small"
                          />
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Fade>
            </Grid>

            {/* Addresses Section */}
            <Grid item xs={12} md={8}>
              <Box ref={addressesRef}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" fontWeight={700}>
                    Saved Addresses
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenAddressDialog()}
                    sx={{
                      borderRadius: 3,
                      px: 3,
                      py: 1,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                      boxShadow: `0 4px 12px ${theme.palette.primary.main}40`
                    }}
                  >
                    Add Address
                  </Button>
                </Box>

                {addresses.length === 0 ? (
                  <Paper
                    sx={{
                      p: 6,
                      textAlign: 'center',
                      borderRadius: 4,
                      bgcolor: 'white',
                      boxShadow: 2
                    }}
                  >
                    <LocationIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No saved addresses
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Add an address to make checkout faster
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenAddressDialog()}
                    >
                      Add Your First Address
                    </Button>
                  </Paper>
                ) : (
                  <Grid container spacing={2}>
                    {addresses.map((address, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Slide direction="up" in timeout={400 + (index * 100)}>
                          <Card
                            id={`address-${index}`}
                            sx={{
                              borderRadius: 3,
                              boxShadow: 3,
                              position: 'relative',
                              overflow: 'hidden',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                boxShadow: 6,
                                transform: 'translateY(-4px)'
                              },
                              border: address.isDefault ? 2 : 1,
                              borderColor: address.isDefault ? 'primary.main' : 'divider'
                            }}
                          >
                            {address.isDefault && (
                              <Chip
                                label="Default"
                                color="primary"
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: 12,
                                  right: 12,
                                  zIndex: 1
                                }}
                              />
                            )}
                            <CardContent sx={{ p: 2.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                                <Avatar
                                  sx={{
                                    bgcolor: 'primary.main',
                                    width: 40,
                                    height: 40
                                  }}
                                >
                                  {getAddressIcon(address.type)}
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="h6" fontWeight={600} gutterBottom>
                                    {address.label || getAddressTypeLabel(address.type)}
                                  </Typography>
                                  <Chip
                                    label={getAddressTypeLabel(address.type)}
                                    size="small"
                                    variant="outlined"
                                    sx={{ mb: 1 }}
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenAddressDialog(index)}
                                    sx={{ color: 'primary.main' }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteAddress(index)}
                                    sx={{ color: 'error.main' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>

                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {address.addressLine1}
                              </Typography>
                              {address.addressLine2 && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {address.addressLine2}
                                </Typography>
                              )}
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {address.city}, {address.state} - {address.pincode}
                              </Typography>
                              {address.phone && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                  <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary">
                                    {address.phone}
                                  </Typography>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Slide>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* Add/Edit Address Dialog */}
        <Dialog
          open={addressDialogOpen}
          onClose={handleCloseAddressDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              overflow: 'hidden'
            }
          }}
        >
          <Box
            sx={{
              height: 80,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}
          >
            <DialogTitle sx={{ color: 'white', pb: 0 }}>
              <Typography variant="h5" fontWeight={700}>
                {editingAddressIndex !== null ? 'Edit Address' : 'Add New Address'}
              </Typography>
            </DialogTitle>
          </Box>
          <DialogContent sx={{ p: 3, pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Address Label (e.g., Home, Work)"
                value={addressForm.label}
                onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })}
                fullWidth
                placeholder="My Home"
              />

              <TextField
                select
                label="Address Type"
                value={addressForm.type}
                onChange={(e) => setAddressForm({ ...addressForm, type: e.target.value })}
                fullWidth
                SelectProps={{
                  native: true
                }}
              >
                <option value="home">Home</option>
                <option value="work">Work</option>
                <option value="other">Other</option>
              </TextField>

              <TextField
                label="Address Line 1"
                value={addressForm.addressLine1}
                onChange={(e) => setAddressForm({ ...addressForm, addressLine1: e.target.value })}
                fullWidth
                required
                multiline
                rows={2}
              />

              <TextField
                label="Address Line 2 (Optional)"
                value={addressForm.addressLine2}
                onChange={(e) => setAddressForm({ ...addressForm, addressLine2: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="City"
                    value={addressForm.city}
                    onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="State"
                    value={addressForm.state}
                    onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                    fullWidth
                    required
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Pincode"
                    value={addressForm.pincode}
                    onChange={(e) => setAddressForm({ ...addressForm, pincode: e.target.value })}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone Number"
                    value={addressForm.phone}
                    onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })}
                    fullWidth
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="checkbox"
                  checked={addressForm.isDefault}
                  onChange={(e) => setAddressForm({ ...addressForm, isDefault: e.target.checked })}
                  id="default-address"
                />
                <label htmlFor="default-address">
                  <Typography variant="body2">Set as default address</Typography>
                </label>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handleCloseAddressDialog} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleSaveAddress}
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                boxShadow: `0 4px 12px ${theme.palette.primary.main}40`
              }}
            >
              Save Address
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default CustomerProfilePage;

